import React from 'react';

import Autocomplete from '@material-ui/lab/Autocomplete';

import * as Elevated from '@pitaman71/omniglot-introspect';
import {  Checkbox, ChipProps, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup, TextField, Theme } from '@material-ui/core';
import { Clients, Streams } from '@pitaman71/omniglot-live-data';

import { Propose } from '..';
import { Styler } from '.';

export function AsCheckbox<ValueType>(props: {
    id: string,
    active?: boolean,
    order?: ValueType[];
    domain?: Elevated.Domain<ValueType>,
    values?: Set<ValueType>,
    client?: Clients.SetClient<ValueType>
    onClick?: (value: ValueType, checked: boolean) => void
}) {
    const asString = props.domain?.asString();     
    const asEnumeration = props.domain?.asEnumeration(10);
    const order = props.order || Array.from(asEnumeration ? asEnumeration.forward() : []);
    return <FormControl component="fieldset">
        <FormGroup>
            { order.map(( value, index ) => {
                const name = asString ? asString.to(value) : `${value}` ;
                return (
                    <FormControlLabel
                        control={<Checkbox id={props.id} checked={props.values?.has(value)} onChange={props.active === false ? undefined : (event) => {
                            if(!event.target.checked) props.client?.remove(value);
                            else props.client?.insert(value);
                            props.onClick && props.onClick(value, event.target.checked);
                        }} name={name} />}
                        label={name}
                    />
                );
            })}
        </FormGroup>
    </FormControl>;
}

function _AsChips<ValueType>(props: {
    id: string,
    active?: boolean,
    icon?: (value?: ValueType) => JSX.Element,
    getTagProps?: (props: {index: number}) => any,
    onClick?: (value: ValueType) => void,
    domain?: Elevated.Domain<ValueType>,
    values?: Set<ValueType>,
    size?: ChipProps['size'],
    limit?: number,
    theme?: Theme
}) {
    const asString = props.domain?.asString();     
    const values_ = Array.from(props.values || []);
    return <React.Fragment>{values_.map(( value, index ) => {
        return <Styler.Chip 
            id={props.id}
            limit={props.limit}
            theme={props.theme}
            size={props.size}
            icon={!props.icon ? undefined : props.icon(value)} 
            label={!asString ? value : asString.to(value)} 
            onClick={props.active === false ? undefined : props.onClick === undefined ? undefined : () => props.onClick && props.onClick(value)}
            {... props.getTagProps ? props.getTagProps({index}) : undefined}
        />
    })}</React.Fragment>
}

export function AsChips<ValueType>(props: {
    id: string,
    active?: boolean,
    icon?: (value?: ValueType) => JSX.Element,
    getTagProps?: (props: {index: number}) => any,
    onClick?: (value: ValueType) => void,
    domain?: Elevated.Domain<ValueType>,
    values?: Set<ValueType>,
    client?: Clients.SetClient<ValueType>
    size?: ChipProps['size'],
    limit?: number,
    theme?: Theme
}) {
    return <_AsChips {...props} domain={props.domain} values={props.values}/>
}

export function AsAutocomplete<ValueType>(props: {
    id: string,
    active?: boolean,
    autoSelect?: boolean,
    icon?: (value?: ValueType) => JSX.Element,
    placeholder?: string,
    style?: React.CSSProperties,
    domain?: Elevated.Domain<ValueType>,
    values?: Set<ValueType>,
    client?: Clients.SetClient<ValueType>
}) {
    const asString = props.domain?.asString();
    const asEnumeration = props.domain?.asEnumeration(100);
    return (
        <Autocomplete
            multiple
            autoSelect={props.autoSelect}
            fullWidth={true}
            id={props.id}
            options={Array.from(asEnumeration?.forward() || [])}
            value={Array.from(props.values || [])}
            freeSolo
            renderTags={(_values, getTagProps) => <_AsChips {...props} values={new Set(_values)} getTagProps={getTagProps}/>}
            renderInput={(params) => (
                <TextField {...params} variant="filled" placeholder={props.placeholder} />
            )}
            onChange={props.active === false ? undefined : (event, values) => { 
                const talents = new Set(values);
                let values_: ValueType[] = [];
                props.values?.forEach(value => {
                    if(talents.has(value)) {
                        talents.delete(value);
                        values_.push(value);
                    }
                })
                Array.from(talents).forEach(value_ => {
                    if(typeof value_ === 'string') {                    
                        if(asString) {
                            const parsed = asString.from(value_);
                            if(parsed !== null)
                                values_.push(parsed);
                        }
                    } else {
                        values_.push(value_);
                    }
                })
                if(values_.length == 0) {
                    props.client?.clear();
                } else {
                    props.client?.assign(new Set(values_));
                }
            }}
        />
    );
}

export function AsRadioButtons<ValueType>(props: {
    id: string,
    active?: boolean,
    domain?: Elevated.Domain<ValueType>,
    values?: Set<ValueType>,
    client?: Clients.SetClient<ValueType>
    makeLabel?: (value_: ValueType) => string
}) {
    const asString = props.domain?.asString();
    const asEnumeration = props.domain?.asEnumeration(100);
    const values = props.values !== undefined ? props.values : new Set(!!asEnumeration ? asEnumeration.forward() : []);
    return <RadioGroup 
        name={`${props.id}-group`}
        value={Array.from(values).reduce((result, value_) => result !== '' ? result : !props.values?.has(value_) ? '' : asString ? asString.to(value_) : `${value_}`, '')} 
        onChange={props.active === false || !props.client ? undefined : (event) => {
            const value_ = !event.target.value ? undefined : asString?.from(event.target.value) || undefined;
            if(value_ === undefined) {
                props.client?.clear();
            } else {
                props.client?.assign(new Set([value_]));
            } 
        }}>
            {
                !asEnumeration ? [] : Array.from(asEnumeration.forward()).map(value => 
                    <FormControlLabel id={`${props.id}-${value}`} value={value} control={<Radio />} label={!!props.makeLabel ? props.makeLabel(value) : `${value}`} />
                )
            }
        </RadioGroup>;
}

